/**
 * PostHog product analytics for the app SPA: autocapture (pageviews + clicks),
 * identified users, session replay (inputs masked), and a small set of named
 * product events.
 *
 * Key-gated and env-gated: analytics only runs in a build that has
 * VITE_POSTHOG_KEY set AND is not the local dev / E2E env. With no key, or in
 * local/E2E, every function here is a no-op, so dev browsing and Playwright
 * runs never send events or record sessions.
 *
 * The distinct id is the Cognito sub (AuthUser.userId): stable, available
 * client-side with no extra round trip, and reproducible server-side via
 * user_identities.provider_sub when backend capture is added later.
 */
import posthog from 'posthog-js';

import { env, isLocal } from './env';

export const analyticsEnabled = Boolean(env.VITE_POSTHOG_KEY) && !isLocal;

let started = false;

/** Initialise PostHog once. Safe to call when disabled (no-ops). */
export function initAnalytics(): void {
  if (!analyticsEnabled || started) return;
  posthog.init(env.VITE_POSTHOG_KEY, {
    api_host: env.VITE_POSTHOG_HOST,
    // Only create person profiles for identified (logged-in) users; keeps
    // anonymous event volume cheap.
    person_profiles: 'identified_only',
    capture_pageview: true,
    autocapture: true,
    // Session replay on, with every input masked (no keystrokes / PII).
    session_recording: { maskAllInputs: true },
  });
  // Tag every event so app vs marketing traffic stays filterable in one project.
  posthog.register({ surface: 'app' });
  started = true;
}

/** Minimal user shape needed to identify; avoids a lib -> features import. */
interface IdentifiableUser {
  userId: string;
  email: string;
  displayName: string | null;
}

/** Tie subsequent events to the logged-in user. */
export function identifyUser(user: IdentifiableUser): void {
  if (!analyticsEnabled) return;
  posthog.identify(user.userId, {
    email: user.email,
    name: user.displayName ?? undefined,
  });
}

/** Clear identity on sign-out so the next user starts a fresh person. */
export function resetAnalytics(): void {
  if (!analyticsEnabled) return;
  posthog.reset();
}

/**
 * Named product events. Defined here so the names stay consistent and are not
 * sprinkled as raw strings across features.
 *
 * These are CLIENT-ONLY events: pure-UI steps with no server round-trip. Events
 * for actions that hit the server are captured on the BACKEND instead (durable,
 * CLAUDE.md §1c): `account_created`, `avatar_created`, `video_generation_started`,
 * `video_generation_completed`, `video_generation_failed`, `onboarding_completed`.
 *
 * Every event (frontend or backend) is documented in docs/ANALYTICS_EVENTS.md
 * (CLAUDE.md §1d). Add the name here AND a doc entry before using it.
 */
export type AnalyticsEvent =
  | 'signup_completed'
  | 'plan_selected'
  | 'trial_started'
  | 'onboarding_experience_answered'
  | 'onboarding_welcome_cta_clicked'
  | 'avatar_wizard_started'
  | 'video_wizard_started'
  | 'video_wizard_step_completed';

export function capture(event: AnalyticsEvent, properties?: Record<string, unknown>): void {
  if (!analyticsEnabled) return;
  posthog.capture(event, properties);
}
